import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "src/Utils/API";
import { StaffRoleDto, CreateStaffRoleRequest } from "../types/staffRole";

type ApiResponseV2<T> = {
  success: boolean;
  message: string | null;
  data: T;
};

type ApiResponse = {
  success: boolean;
  message: string | null;
};

export const roleKeys = {
  all: ["organization", "staff-roles"] as const,
  detail: (id: string) => ["organization", "staff-roles", id] as const,
};

// GET /web/staff-roles — scoped to caller's organization
export const useOrganizationRoles = () =>
  useQuery({
    queryKey: roleKeys.all,
    queryFn: async (): Promise<StaffRoleDto[]> => {
      const res = await fetch(`${API_URL}/staff-roles`, {
        credentials: "include",
      });
      const body: ApiResponseV2<StaffRoleDto[]> = await res.json();
      if (!body.success) throw new Error(body.message ?? "Failed to fetch roles");
      return body.data;
    },
  });

// GET /web/staff-roles/{id}
export const useOrganizationRole = (id: string | null | undefined) =>
  useQuery({
    queryKey: roleKeys.detail(id ?? ""),
    queryFn: async (): Promise<StaffRoleDto> => {
      const res = await fetch(`${API_URL}/staff-roles/${id}`, {
        credentials: "include",
      });
      const body: ApiResponseV2<StaffRoleDto> = await res.json();
      if (!body.success) throw new Error(body.message ?? "Failed to fetch role");
      return body.data;
    },
    enabled: !!id,
  });

// POST /web/staff-roles
export const useCreateOrganizationRole = (
  onSuccess?: (role: StaffRoleDto) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: CreateStaffRoleRequest
    ): Promise<StaffRoleDto> => {
      const res = await fetch(`${API_URL}/staff-roles`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body: ApiResponseV2<StaffRoleDto> = await res.json();
      if (!body.success) throw new Error(body.message ?? "Failed to create role");
      return body.data;
    },
    onSuccess: (role) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
      onSuccess?.(role);
    },
  });
};

// DELETE /web/staff-roles/{id}
export const useDeleteOrganizationRole = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_URL}/staff-roles/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const body: ApiResponse = await res.json();
      if (!body.success) throw new Error(body.message ?? "Failed to delete role");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
      onSuccess?.();
    },
  });
};

// POST /web/staff-roles/{id}/permissions — attach
export const useAttachRolePermissions = (
  onSuccess?: (role: StaffRoleDto) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      roleId,
      permissionIds,
    }: {
      roleId: string;
      permissionIds: string[];
    }): Promise<StaffRoleDto> => {
      const res = await fetch(`${API_URL}/staff-roles/${roleId}/permissions`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ permissionIds }),
      });
      const body: ApiResponseV2<StaffRoleDto> = await res.json();
      if (!body.success) throw new Error(body.message ?? "Failed to attach permissions");
      return body.data;
    },
    onSuccess: (role) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(role.id) });
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
      onSuccess?.(role);
    },
  });
};

// DELETE /web/staff-roles/{id}/permissions — detach
export const useDetachRolePermissions = (
  onSuccess?: (role: StaffRoleDto) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      roleId,
      permissionIds,
    }: {
      roleId: string;
      permissionIds: string[];
    }): Promise<StaffRoleDto> => {
      const res = await fetch(`${API_URL}/staff-roles/${roleId}/permissions`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ permissionIds }),
      });
      const body: ApiResponseV2<StaffRoleDto> = await res.json();
      if (!body.success) throw new Error(body.message ?? "Failed to detach permissions");
      return body.data;
    },
    onSuccess: (role) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(role.id) });
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
      onSuccess?.(role);
    },
  });
};
